/**
 * Display-name resolution.
 *
 * Two helpers with parallel fallback chains:
 *
 * - `getDisplayLabel(model, plural?)` — for **model class names**.
 *   Used in page headers, list-view titles, browser tab titles.
 *     1. `displayOverrides[model]` (per-app config)
 *     2. `modelDisplayConfig[model]` (codegen, raw Neo4j label)
 *     3. CamelCase split of `model`
 *
 * - `getRelationshipLabel(sourceModel, relName)` — for **relationship
 *   labels** on a specific source model. Used in tab strips.
 *     1. `relationshipDisplay[sourceModel][relName].label` (per-app config)
 *     2. Humanized rel name ("canonical_unit" → "Canonical Unit")
 *
 * Type names go through `getDisplayLabel`. Rel names go through
 * `getRelationshipLabel`. Tabs labelled by rel name preserve
 * cardinality semantics (singular for ToOne, plural for ToMany)
 * because the rel name itself encodes them.
 */

let _overrides: Record<string, { label?: string; labelPlural?: string }> = {};
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require('@/config/display-overrides');
  _overrides = mod.displayOverrides || {};
} catch {
  // No overrides file in this app — fall through to codegen labels.
}

let _displayConfig: Record<string, { label: string; labelPlural: string }> = {};
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require('@sourcepilot/generated/registries/model-display-config');
  _displayConfig = mod.modelDisplayConfig || {};
} catch {
  // model-display-config not generated yet.
}

type RelationshipMode = 'tab' | 'card' | 'hidden';
let _relOverrides: Record<
  string,
  Record<string, { label?: string; mode?: RelationshipMode }>
> = {};
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require('@/config/relationship-display');
  _relOverrides = mod.relationshipDisplay || {};
} catch {
  // No relationship-display config in this app.
}

function _camelCaseSplit(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
}

/**
 * Resolve a friendly display label for a model class name.
 *
 * @param model - Class name (e.g. "PropertyType", "PDMProduct")
 * @param plural - When true, return the plural form
 */
export function getDisplayLabel(model: string, plural: boolean = false): string {
  const override = _overrides[model];
  if (override) {
    if (plural && override.labelPlural) return override.labelPlural;
    if (!plural && override.label) return override.label;
  }
  const cfg = _displayConfig[model];
  if (cfg) {
    return plural ? cfg.labelPlural : cfg.label;
  }
  const split = _camelCaseSplit(model);
  return plural ? `${split}s` : split;
}

/**
 * Resolve a friendly tab/heading label for a relationship on a source
 * model. Falls back to the humanized rel name when no override is
 * configured.
 *
 * The rel name's natural form carries cardinality:
 * `canonical_unit` → "Canonical Unit" (singular ToOne),
 * `sub_requirements` → "Sub Requirements" (plural ToMany). Don't
 * try to be smart about plural — let the rel name speak.
 *
 * @param sourceModel - Class name that owns the relationship
 * @param relName - Property name on the source (e.g. "canonical_unit")
 */
export function getRelationshipLabel(
  sourceModel: string,
  relName: string,
): string {
  const override = _relOverrides[sourceModel]?.[relName]?.label;
  if (override) return override;
  return relName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolve render mode for a relationship on a source model.
 *
 * Defaults (no config):
 *   - Cardinality "one" → "card"
 *   - Cardinality "many" → "tab"
 *
 * Override via `relationshipDisplay[model][rel].mode` to force a
 * specific mode (e.g. hide a noisy rel, or render a ToOne as a tab).
 *
 * Note: server-side framework hides (e.g. `security_workspace`,
 * names starting with `_`) never reach the client manifest, so no
 * runtime hide list is needed here.
 */
export function getRelationshipMode(
  sourceModel: string,
  relName: string,
  cardinality: 'one' | 'many',
): RelationshipMode {
  const override = _relOverrides[sourceModel]?.[relName]?.mode;
  if (override) return override;
  return cardinality === 'one' ? 'card' : 'tab';
}
